/**
 * Page-context extraction. Reads metadata + structural signals from the
 * host page on widget mount and packages them for transmission with each
 * /v1/speak call. The backend uses this to bias voice selection and
 * ambiguous-token expansion.
 *
 * What's collected (metadata only, never page body text):
 *   - <html lang>        - e.g. "cy-GB", "en"
 *   - <title>            - up to 200 chars
 *   - description meta   - first match of <meta name="description"> or og:description, up to 500
 *   - h1                 - first <h1>, up to 100 chars
 *   - h2s                - first 5 <h2>s, up to 80 chars each
 *
 * The toggle in the settings panel gates whether this object is included
 * in the request body at all (defaults: on for pk_live_*, off for pk_test_*).
 */

export type PageContext = {
  lang?: string;
  title?: string;
  description?: string;
  headings?: string[];
};

const TITLE_MAX = 200;
const DESC_MAX = 500;
// Both h1 and h2 trim to 80 because the backend Zod schema bounds every
// `headings` entry at `z.string().max(80)`. A 100-char h1 would get
// rejected as `invalid_request` whenever pageContext was enabled.
// Codex review on Llais#20.
const HEADING_MAX = 80;
const H2_COUNT = 5;

function trimTo(s: string | null | undefined, max: number): string | undefined {
  if (!s) return undefined;
  const t = s.trim().replace(/\s+/g, ' ');
  if (!t) return undefined;
  return t.length > max ? t.slice(0, max) : t;
}

function metaContent(name: string): string | undefined {
  // Try both name=... and property=... forms (the latter for og: tags).
  const sel =
    `meta[name="${name}"], meta[property="${name}"]`;
  const el = document.querySelector<HTMLMetaElement>(sel);
  return el?.content ?? undefined;
}

export function extractPageContext(): PageContext {
  const ctx: PageContext = {};

  try {
    const lang = document.documentElement.lang?.trim();
    if (lang) ctx.lang = lang.slice(0, 16);
  } catch {
    /* ignore */
  }

  try {
    const title = trimTo(document.title, TITLE_MAX);
    if (title) ctx.title = title;
  } catch {
    /* ignore */
  }

  try {
    const desc =
      trimTo(metaContent('description'), DESC_MAX) ??
      trimTo(metaContent('og:description'), DESC_MAX);
    if (desc) ctx.description = desc;
  } catch {
    /* ignore */
  }

  try {
    const headings: string[] = [];
    const h1 = trimTo(document.querySelector('h1')?.textContent, HEADING_MAX);
    if (h1) headings.push(h1);
    const h2s = Array.from(document.querySelectorAll('h2')).slice(0, H2_COUNT);
    for (const h of h2s) {
      const t = trimTo(h.textContent, HEADING_MAX);
      if (t) headings.push(t);
    }
    if (headings.length > 0) ctx.headings = headings;
  } catch {
    /* ignore */
  }

  return ctx;
}

/**
 * Compute the default for the pageContext toggle based on the API key
 * prefix. Live keys are origin-locked + paid → context-on by default
 * (better quality, trusted use). Test keys are open demos → context-off
 * by default (more privacy-conservative for hands-off hands).
 */
export function defaultPageContextEnabled(apiKey: string): boolean {
  return apiKey.startsWith('pk_live_');
}
